(ns common.schema.schema-utils
  (:require
   [clojure.string :as string]
   [common.currencies :as currencies]
   [common.data-types :as data-types]
   [common.number-utils :as nu]
   [common.schema.constraints :as constraints]
   [common.schema.data-types :as schema-data-types]
   [common.schema.field-spec :as field-spec]
   [common.util :as cu]
   [malli.core :as malli]))

;; ------------------------------------------------------------------------------
;; added: search capability overrides (validation + merge)
;; ------------------------------------------------------------------------------

(def search-capability-keys
  [:search/indexed?
   :search/full-text?
   :search/sortable?
   :search/facet?
   :search/filter
   :search/large-text?
   :search/high-cardinality?
   :search/materialized])

(def search-filter-capability-keys
  [:terms? :prefix? :range?])

(defn- validate-search-capabilities!
  "Throw if unknown :search/... keys are present in opts."
  [opts]
  (let [search-keys (filter #(= "search" (namespace %)) (keys opts))
        unknown (seq (remove (set search-capability-keys) search-keys))]
    (when unknown
      (throw (ex-info "Unknown :search capability key(s) in schema opts"
                      {:invalid unknown :opts (select-keys opts search-keys)})))))

(def ^:private qualified->unqualified-field-option
  "Field-helper options that are destructured unqualified and emitted by the
  helper under their :field/-qualified key, keyed by the qualified form a call
  site might reach for instead."
  {:field/option-filter-fn   :option-filter-fn
   :field/os-filter-terms-fn :os-filter-terms-fn
   :field/deep-indexing?     :deep-indexing?
   :field/live-update-paths  :live-update-paths
   :field/no-history?        :no-history?})

(def ^:private removed-field-options
  "Options a helper once accepted but that could never reach anything, keyed by
  the reason to give the call site. Rejected explicitly rather than just deleted
  from the destructuring, because an unrecognised unqualified option is dropped
  in silence - the same silence that let the broken one ship."
  {:placeholder
   (str "a field has no placeholder slot. This nested {:placeholder {:en ...}} "
        "into :field/translations, which is a locale->string map, so the field "
        "could not validate; and :field/placeholder is in neither the registry's "
        "validation allowlist nor any entity-field editor, so emitting it "
        "instead would be dead metadata.")})

(defn- assert-field-options!
  "Throw if an options map carries something the helper will silently ignore:
  the qualified form of an option destructured unqualified, or an option listed
  in `removed-field-options`.

  Both share one failure mode. Only destructured keys survive - the options map
  is also passed to merge-search-capabilities as `overrides`, which keeps just
  its :search/* keys - so anything else is dropped with no signal at the call
  site. That is how :common/owner shipped with an OpenSearch filter and no local
  one, how the stage name fields' option filter never reached the schema at all,
  and how :placeholder produced a field that could not validate (see
  ai/mistakes.md)."
  [attribute overrides]
  (doseq [[qualified unqualified] qualified->unqualified-field-option]
    (when (contains? overrides qualified)
      (throw (ex-info (str "Field option " qualified " is ignored - pass "
                           unqualified " instead")
                      {:attribute attribute
                       :given     qualified
                       :expected  unqualified}))))
  (doseq [[removed reason] removed-field-options]
    (when (contains? overrides removed)
      (throw (ex-info (str "Field option " removed " is not supported - " reason)
                      {:attribute attribute
                       :given     removed}))))
  (when (and (contains? overrides :live-update-paths)
             (not (malli/validate field-spec/LiveUpdatePaths
                                  (:live-update-paths overrides))))
    (throw (ex-info (str "Field option :live-update-paths accepts only [] or "
                         "[:live-paths/all]. Explicit paths are not implemented, "
                         "and a path that nothing reads would look like one that works.")
                    {:attribute attribute
                     :given     (:live-update-paths overrides)
                     :allowed   [[] [:live-paths/all]]}))))

(defn defaults-by-data-type
  [data-type]
  (let [dt (get schema-data-types/zapflow-data-types data-type)
        {:keys [default-editor default-filter allowed-editor-types allowed-filter-types]} dt]
    {:editor                  default-editor
     :grid                    default-editor
     :filter                  default-filter
     :ui/default-editor-type  default-editor
     :ui/default-filter-type  default-filter
     :ui/allowed-editor-types allowed-editor-types
     :ui/allowed-filter-types allowed-filter-types
     :field/system-mandatory  nil
     :validation-spec         nil}))

(defn- field-system-options
  "Options that every helper passes through merge-search-capabilities.
  `:no-history?` becomes `:field/no-history?`, which asks for `:db/noHistory`
  on the Datomic attribute (see common.schema.field-spec)."
  [{:keys [system-semantics system-required? no-history?] :as opts}]
  (cond-> {}
    system-semantics (assoc :field/system-semantics system-semantics)
    (contains? opts :system-required?) (assoc :field/system-required? system-required?)
    (contains? opts :no-history?) (assoc :field/no-history? no-history?)))

(defn- merge-search-capabilities
  "Merge sanitized :search/... capability overrides into field metadata.
   - validates top-level :search/* keys
   - sanitizes nested :search/filter to only known keys"
  [field-metadata opts]
  (validate-search-capabilities! opts)
  (let [search-overrides (select-keys opts search-capability-keys)
        filter-overrides (get-in opts [:search/filter])
        sanitized-filter (when filter-overrides
                           (select-keys filter-overrides search-filter-capability-keys))
        defaults (defaults-by-data-type (:field/data-type field-metadata))
        system-overrides (field-system-options opts)]
    (cond-> (merge defaults field-metadata search-overrides system-overrides)
      sanitized-filter (assoc-in [:search/filter] sanitized-filter))))

(def system-created-field-semantics
  {:field.system/value-control :field.value-control/system-only
   :field.system/nullability   :field.nullability/system-required
   :field.system/clearability  :field.clearability/not-clearable-after-set
   :field.system/modifiability :field.modifiability/create-only})

(def system-updated-field-semantics
  {:field.system/value-control :field.value-control/system-only
   :field.system/nullability   :field.nullability/system-required
   :field.system/clearability  :field.clearability/not-clearable-after-set
   :field.system/modifiability :field.modifiability/system-only})

(def system-prefilled-required-field-semantics
  {:field.system/value-control :field.value-control/system-prefilled
   :field.system/nullability   :field.nullability/system-required})

(def user-provided-system-required-field-semantics
  {:field.system/value-control :field.value-control/user-provided
   :field.system/nullability   :field.nullability/system-required
   :field.system/clearability  :field.clearability/not-clearable-after-set})

(def system-controlled-optional-field-semantics
  {:field.system/value-control :field.value-control/system-only
   :field.system/modifiability :field.modifiability/system-only})

(def system-controlled-required-field-semantics
  {:field.system/value-control :field.value-control/system-only
   :field.system/nullability   :field.nullability/system-required
   :field.system/clearability  :field.clearability/not-clearable-after-set
   :field.system/modifiability :field.modifiability/system-only})

(def system-controlled-not-clearable-field-semantics
  {:field.system/value-control :field.value-control/system-only
   :field.system/clearability  :field.clearability/not-clearable-after-set
   :field.system/modifiability :field.modifiability/system-only})

;; ------------------------------------------------------------------------------
;; existing helpers & constants
;; ------------------------------------------------------------------------------

;; These helpers still write legacy :field/mandatory metadata while the schema
;; target evolves. Runtime consumers should interpret it through
;; common.schema.field-spec helpers rather than reading the key directly.

(defn composite-text-field
  [composite-attributes delim
   {:keys [entity-data entity-lookup get-field-schema display-string]}]
  (string/join delim
               (->> composite-attributes
                    (map (fn [a]
                           (let [fs (get-field-schema a)
                                 data-type (:field/data-type fs)
                                 value (get entity-data a)]
                             (case data-type
                               :data.type/ref-single (display-string (if (:db/id value)
                                                                       value
                                                                       (entity-lookup value)))
                               (str value)))))
                    (remove nil?))))

(defn fund-field-in-use?
  [_entity entity-types-lookup has-feature-access-fn _has-entity-access?]
  (or (has-feature-access-fn :feature/fund-management)
      (not-empty (:entity-type/fund entity-types-lookup))))

(defn pipeline-in-use?
  [entity entity-types-lookup _has-feature-access-fn has-entity-access?]
  (let [available-pipelines (->> entity-types-lookup
                                 :entity.type/pipeline
                                 (filter
                                  (fn [p]
                                    (and (has-entity-access? p)
                                         (= (:pipeline/entity-type p) (:entity/type entity))))))
        entity-pipeline (or (:oppo/pipeline entity)
                            (:fundraising-opportunity/pipeline entity)
                            (:sales-opportunity/pipeline entity)
                            (:exit-opportunity/pipeline entity))
        res (or (not entity-pipeline)                       ;available if not set
                (> (count available-pipelines) 1))]
    res))

(def access-system-user
  {:access/write :role/system
   :access/read  :role/user})
(def access-system-ssg
  {:access/write :role/system
   :access/read  :role/guest-sell-side})
(def access-system-lpg
  {:access/write :role/system
   :access/read  :role/guest-lp-portal})

(def access-zadmin-lpg
  {:access/write :role/zadmin
   :access/read  :role/guest-lp-portal})

(def access-system-none
  {:access/write :role/system
   :access/read  :role/none})
(def access-system-owner
  {:access/write :role/system
   :access/read  :role/account-owner})
(def access-owner-owner
  {:access/write :role/account-owner
   :access/read  :role/account-owner})
(def access-system-system
  {:access/write :role/system
   :access/read  :role/system})
(def access-sys-admin-sys-admin
  {:access/write :role/sys-admin
   :access/read  :role/sys-admin})
(def access-sys-admin-user
  {:access/write :role/sys-admin
   :access/read  :role/user})
(def access-zadmin-zadmin
  {:access/write :role/zadmin
   :access/read  :role/zadmin})
(def access-zadmin-admin
  {:access/write :role/zadmin
   :access/read  :role/admin})
(def access-zadmin-user
  {:access/write :role/zadmin
   :access/read  :role/user})
(def access-zadmin-bsg
  {:access/write :role/zadmin
   :access/read  :role/guest-buy-side})
(def access-zadmin-ssg
  {:access/write :role/zadmin
   :access/read  :role/guest-sell-side})
(def access-zadmin-none
  {:access/write :role/zadmin
   :access/read  :role/none})

(def access-system-zadmin
  {:access/write :role/system
   :access/read  :role/zadmin})

(def access-system-admin
  {:access/write :role/system
   :access/read  :role/admin})

(def access-system-sys-admin
  {:access/write :role/system
   :access/read  :role/sys-admin})
(def access-system-authenticated
  {:access/write :role/system
   :access/read  :role/authenticated})
(def access-user-authenticated
  {:access/write :role/user
   :access/read  :role/authenticated})
(def access-admin-admin
  {:access/write :role/admin
   :access/read  :role/admin})
(def access-admin-system
  {:access/write :role/admin
   :access/read  :role/system})
(def access-admin-user
  {:access/write :role/admin
   :access/read  :role/user})
(def access-admin-bsg
  {:access/write :role/admin
   :access/read  :role/guest-buy-side})
(def access-admin-ssg
  {:access/write :role/admin
   :access/read  :role/guest-sell-side})
(def access-admin-none
  {:access/write :role/admin
   :access/read  :role/none})
(def access-user-user
  {:access/write :role/user
   :access/read  :role/user})
(def access-user-lpg
  {:access/write :role/user
   :access/read  :role/guest-lp-portal})
(def access-bsg-bsg
  {:access/write :role/guest-buy-side
   :access/read  :role/guest-buy-side})
(def access-ssg-ssg
  {:access/write :role/guest-sell-side
   :access/read  :role/guest-sell-side})

(def access-sug-sug
  {:access/write :role/guest-survey
   :access/read  :role/guest-survey})

(def access-ssg-lpg
  {:access/write :role/guest-sell-side
   :access/read  :role/guest-lp-portal})

(def access-user-ssg
  {:access/write :role/user
   :access/read  :role/guest-sell-side})

(def access-user-sug
  {:access/write :role/user
   :access/read  :role/guest-survey})

(def access-system-sug
  {:access/write :role/system
   :access/read  :role/guest-survey})

(def access-cdg-cdg
  {:access/write :role/guest-counterparty-diligence
   :access/read  :role/guest-counterparty-diligence})

(def access-ssg-none
  {:access/write :role/guest-sell-side
   :access/read  :role/none})

(def access-user-none
  {:access/write :role/user
   :access/read  :role/none})

(def access-none-none
  {:access/write :role/none
   :access/read  :role/none})

(defn agg-sum
  [optional-values]
  (->> optional-values
       (map nu/->float)
       (apply +)))

(defn agg-avg-skip-empty
  [optional-values]
  (->> optional-values
       (remove nil?)
       (nu/average)))

(defn agg-avg
  [optional-values]
  (->> optional-values
       (nu/average)))

(defn avg-mult
  [optional-values]
  (->> optional-values
       (keep (fn [v]
               (cond (number? v)
                     v
                     (string? v)
                     (data-types/parse-edn-string (string/replace v "x" "")))))
       (map float)
       (nu/average)))

;; ------------------------------------------------------------------------------
;; fields that were not modified to add :search overrides are left intact.
;; all functions below now accept :search/... in opts and merge+validate them.
;; ------------------------------------------------------------------------------

(defn name-unique-global
  [attribute label {:keys [authorization editor-type] :as overrides}]
  [[attribute
    (merge-search-capabilities
     (merge
      {:field/attribute             attribute
       :field/data-type             :data.type/text-simple
       :field/name-attribute        attribute
       :field/mandatory             :mandatory/always
       :field/default-visibility    (merge {:editor           :editor.type/link
                                            :grid             :editor.type/link
                                            :filter           :filter.type/text
                                            :field-visibility :in-use-disabled}
                                           (when editor-type
                                             {:editor editor-type}))
       :field/default-authorization (merge
                                     {:access/read  :role/none
                                      :access/write :role/zadmin}
                                     (when authorization
                                       authorization))
       :field/lower-case-str?       true
       :field/constraints           {:constraint/string-min-length 1
                                     :constraint/unique-global     true}
       :field/translations          {:en label}
       :validation-spec             string?
       :field/name                  label
       :ui/default-editor-type      :editor.type/text-simple
       :ui/allowed-editor-types     #{:editor.type/text-simple}
       :ui/default-filter-type      :filter.type/text
       :ui/allowed-filter-types     #{:filter.type/text}})
     overrides)]])

(defn text-unique-global
  [attribute label {:keys [authorization editor-type lower-case-str? constraints] :as overrides}]
  [[attribute
    (merge-search-capabilities
     (merge {:field/attribute             attribute
             :field/data-type             :data.type/text-simple
             :field/default-visibility    (merge {:editor           :editor.type/link
                                                  :grid             :editor.type/link
                                                  :filter           :filter.type/text
                                                  :field-visibility :in-use}
                                                 (when editor-type
                                                   {:editor editor-type}))
             :field/default-authorization (merge
                                           {:access/read  :role/none
                                            :access/write :role/zadmin}
                                           (when authorization
                                             authorization))
             :field/constraints           (merge
                                           constraints
                                           {:constraint/string-min-length 1
                                            :constraint/unique-global     true})
             :field/translations          {:en label}
             :validation-spec             string?
             :field/name                  label
             :ui/default-editor-type      :editor.type/text-simple
             :ui/allowed-editor-types     #{:editor.type/text-simple}
             :ui/default-filter-type      :filter.type/text
             :ui/allowed-filter-types     #{:filter.type/text}}
            (when lower-case-str?
              {:field/lower-case-str? true}))
     overrides)]])

(def email-validation-spec
  [:and
   string?
   [:re #"^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$"]
   [:fn
    {:error/message "Invalid email format"}
    (fn [email]
      (and
        ;; Basic length constraints
       (>= (count email) 5)
       (<= (count email) 320)                              ; RFC 5321 limit

        ;; Split into local and domain parts
       (let [parts (clojure.string/split email #"@")]
         (and
          (= (count parts) 2)
          (let [[local domain] parts]
            (and
                ;; Local part validation
             (>= (count local) 1)
             (<= (count local) 64)                       ; RFC 5321 limit
             (not (clojure.string/starts-with? local "."))
             (not (clojure.string/ends-with? local "."))
             (not (re-find #"\.\." local))               ; No consecutive dots

                ;; Domain part validation
             (>= (count domain) 1)
             (<= (count domain) 253)                     ; RFC 5321 limit
             (not (clojure.string/starts-with? domain "."))
             (not (clojure.string/ends-with? domain "."))
             (not (re-find #"\.\." domain))              ; No consecutive dots
             (re-find #"\." domain)                      ; Must contain at least one dot

                ;; Domain labels validation
             (let [labels (clojure.string/split domain #"\.")]
               (and
                (every? #(and (>= (count %) 1)
                              (<= (count %) 63)
                              (re-matches #"[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?" %))
                        labels)
                    ;; TLD validation (last label should be at least 2 chars, letters only)
                (let [tld (last labels)]
                  (and (>= (count tld) 2)
                       (re-matches #"[a-zA-Z]+" tld)))))))))))]])

;; Simpler version for basic email validation
(def simple-email-validation-spec
  [:and
   string?
   [:re #"^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$"]
   [:fn
    {:error/message "Email must be between 5 and 320 characters"}
    #(and (>= (count %) 5) (<= (count %) 320))]])

;; Very strict RFC 5322 compliant version (more complex but accurate)
(def strict-email-validation-spec
  [:and
   string?
   [:re #"^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?$"]
   [:fn
    {:error/message "Invalid email format"}
    (fn [email]
      (and
       (>= (count email) 5)
       (<= (count email) 320)
       (let [at-index (.lastIndexOf email "@")]
         (and
          (pos? at-index)
          (< at-index (dec (count email)))
          (<= (count (subs email 0 at-index)) 64)         ; local part max 64 chars
          (<= (count (subs email (inc at-index))) 253)))))]]) ; domain part max 253 chars

(defn email-field
  [attribute label {:keys [authorization default-field-visibility lower-case-str?] :as overrides}]
  [[attribute
    (merge-search-capabilities
     (merge {:field/attribute             attribute
             :field/data-type             :data.type/text-simple
             :field/default-visibility    (merge {:editor           :editor.type/email
                                                  :grid             :editor.type/email
                                                  :filter           :filter.type/text
                                                  :field-visibility :not-in-use}
                                                 (when default-field-visibility
                                                   {:field-visibility default-field-visibility}))
             :field/constraints           {:constraint/string-min-length 1
                                           :constraint/valid-email       true}
             :field/default-authorization (merge {:access/read  :role/user
                                                  :access/write :role/user}
                                                 (when authorization
                                                   authorization))
             :field/translations          {:en label}
             :validation-spec             simple-email-validation-spec
             :field/name                  label
             :ui/default-editor-type      :editor.type/email
             :ui/allowed-editor-types     #{:editor.type/email}
             :ui/default-filter-type      :filter.type/text
             :ui/allowed-filter-types     #{:filter.type/text}}
            (when lower-case-str?
              {:field/lower-case-str? true}))
     overrides)]])

(def multi-email-validation-spec
  [:and
   [:or vector? set?]                                       ; Accept either vector or set
   [:fn
    {:error/message "Must contain at least one email"}
    seq]                                               ; Must not be empty
   [:fn
    {:error/message "Cannot contain more than 10 emails"}
    #(<= (count %) 10)]                                     ; Reasonable limit
   [:every simple-email-validation-spec]])                  ; Each item must be a valid email

;; Alternative version that also accepts comma-separated string input
(def flexible-multi-email-validation-spec
  [:or
   ;; Accept collection of emails
   [:and
    [:or vector? set?]
    [:fn seq]                                          ; Must not be empty
    [:fn #(<= (count %) 10)]                                ; Reasonable limit
    [:every simple-email-validation-spec]]

   ;; Accept comma-separated string
   [:and
    string?
    [:fn
     {:error/message "Invalid email format in comma-separated list"}
     (fn [email-string]
       (let [emails (->> (clojure.string/split email-string #"[,;]")
                         (map clojure.string/trim)
                         (remove empty?))]
         (and
          (seq emails)                                     ; Must have at least one email
          (<= (count emails) 10)                           ; Reasonable limit
          (every? #(malli/validate simple-email-validation-spec %) emails))))]]])

;; More permissive version for optional multi-email fields
(def optional-multi-email-validation-spec
  [:or
   nil?
   [:and
    [:or vector? set?]
    [:every simple-email-validation-spec]]])

;; Strict version with more constraints
(def strict-multi-email-validation-spec
  [:and
   [:or vector? set?]
   [:fn
    {:error/message "Must contain between 1 and 5 emails"}
    #(and (seq %) (<= (count %) 5))]
   [:fn
    {:error/message "Cannot contain duplicate emails"}
    (fn [emails]
      (= (count emails) (count (distinct (map clojure.string/lower-case emails)))))]
   [:every strict-email-validation-spec]])

(defn email-field-multi
  [attribute label {:keys [authorization default-field-visibility lower-case-str?] :as overrides}]
  [[attribute
    (merge-search-capabilities
     (merge {:field/attribute             attribute
             :field/data-type             :data.type/text-multi
             :field/default-visibility    (merge {:editor           :editor.type/email-multi
                                                  :grid             :editor.type/email-multi
                                                  :filter           :filter.type/text
                                                  :field-visibility :not-in-use}
                                                 (when default-field-visibility
                                                   {:field-visibility default-field-visibility}))
             :field/constraints           {:constraint/string-min-length 1
                                           :constraint/valid-email       true}
             :field/default-authorization (merge {:access/read  :role/user
                                                  :access/write :role/user}
                                                 (when authorization
                                                   authorization))
             :field/translations          {:en label}
             :validation-spec             multi-email-validation-spec
             :field/name                  label
             :ui/default-editor-type      :editor.type/email-multi
             :ui/allowed-editor-types     #{:editor.type/email-multi}
             :ui/default-filter-type      :filter.type/text
             :ui/allowed-filter-types     #{:filter.type/text}}
            (when lower-case-str?
              {:field/lower-case-str? true}))
     overrides)]])

(defn url-field-multi
  [attribute label {:keys [authorization default-field-visibility lower-case-str?] :as overrides}]
  [[attribute
    (merge-search-capabilities
     (merge {:field/attribute             attribute
             :field/data-type             :data.type/url-multi
             :field/default-visibility    (merge {:editor           :editor.type/url-multi
                                                  :grid             :editor.type/url-multi
                                                  :filter           :filter.type/text
                                                  :field-visibility :not-in-use}
                                                 (when default-field-visibility
                                                   {:field-visibility default-field-visibility}))
             :field/constraints           {:constraint/string-min-length 1
                                           :constraint/valid-email       true}
             :field/default-authorization (merge {:access/read  :role/user
                                                  :access/write :role/user}
                                                 (when authorization
                                                   authorization))
             :field/translations          {:en label}
             :validation-spec             string?
             :field/name                  label
             :ui/default-editor-type      :editor.type/url
             :ui/allowed-editor-types     #{:editor.type/url}
             :ui/default-filter-type      :filter.type/text
             :ui/allowed-filter-types     #{:filter.type/text}}
            (when lower-case-str?
              {:field/lower-case-str? true}))
     overrides)]])

(defn name-unique-with-another-attribute-field
  [attribute label entity-type other-attribute {:keys [option-filter-fn] :as overrides}]
  (assert-field-options! attribute overrides)
  [[attribute
    (merge-search-capabilities
     (merge
      {:field/attribute             attribute
       :field/data-type             :data.type/text-simple
       :field/name-attribute        attribute
       :field/mandatory             :mandatory/always
       :field/default-visibility    {:editor           :editor.type/text-simple
                                     :grid             :editor.type/text-simple
                                     :filter           :filter.type/text
                                     :field-visibility :in-use-disabled}
       :field/default-authorization {:access/read  :role/user
                                     :access/write :role/user}
       :field/lower-case-str?       true
       :field/constraints           {:constraint/string-min-length
                                     1
                                     :constraint/unique-tenant-ignore-case-additional-attribute
                                     {:validation-fn
                                      (partial
                                       constraints/tenant-unique-ignore-case-additional-attribute
                                       attribute
                                       other-attribute
                                       [entity-type])}}
       :field/translations          {:en label}
       :field/name                  label
       :validation-spec             string?
       :ui/default-editor-type      :editor.type/text-simple
       :ui/allowed-editor-types     #{:editor.type/text-simple}
       :ui/default-filter-type      :filter.type/text
       :ui/allowed-filter-types     #{:filter.type/text}}
      (when option-filter-fn
        {:field/option-filter-fn option-filter-fn}))
     overrides)]])

(defn name-unique-per-tenant
  [attribute label entity-type {:keys [authentication] :as custom-config}]
  [[attribute
    (merge
     {:field/attribute             attribute
      :field/data-type             :data.type/text-simple
      :field/mandatory             :mandatory/always
      :field/name-attribute        attribute
      :field/lower-case-str?       true
      :field/constraints           {:constraint/mandatory-fn      (fn [field-value _entity-data]
                                                                    (empty? (cu/str-trim-lcase
                                                                             field-value)))
                                    :constraint/string-min-length 1
                                    :constraint/unique-tenant-ignore-case
                                    {:validation-fn (partial constraints/tenant-unique-ignore-case
                                                             attribute
                                                             [entity-type])}}
      :field/default-visibility    {:editor           :editor.type/text-simple
                                    :grid             :editor.type/text-simple
                                    :filter           :filter.type/text
                                    :field-visibility :in-use-disabled}
      :field/default-authorization (merge {:access/read  :role/user
                                           :access/write :role/user}
                                          authentication)
      :field/not-clearable?        true
      :field/translations          {:en label}
      :field/name                  label
      :validation-spec             string?
      :ui/default-editor-type      :editor.type/text-simple
      :ui/allowed-editor-types     #{:editor.type/text-simple}
      :ui/default-filter-type      :filter.type/text
      :ui/allowed-filter-types     #{:filter.type/text}}
     (field-system-options custom-config)
     (dissoc custom-config :system-required? :system-semantics))]])

(defn currency-value-field
  [attribute label
   {:keys [default-field-visibility create-visibility authorization symbol-attribute
           short-name constraints]
    :as   overrides}]
  [[attribute
    (merge-search-capabilities
     (merge {:field/attribute             attribute
             :field/data-type             :data.type/currency-value
             :field/constraints           {:constraint/smaller-than-inclusive 9007199254740991}
             :field/default-visibility    (merge {:editor           :editor.type/number
                                                  :grid             :editor.type/number
                                                  :filter           :filter.type/number
                                                  :field-visibility :not-in-use}
                                                 (when default-field-visibility
                                                   {:field-visibility default-field-visibility}))
             :field/aggregate-total-fn    agg-sum
             :field/default-authorization (merge
                                           {:access/read  :role/user
                                            :access/write :role/user}
                                           (when authorization
                                             authorization))
             :field/translations          {:en label}
             :field/name                  label
             :validation-spec             number?
             :ui/default-editor-type      :editor.type/number
             :ui/allowed-editor-types     #{:editor.type/number}
             :ui/default-filter-type      :filter.type/number
             :ui/allowed-filter-types     #{:filter.type/number}}
            (when short-name
              {:field/short-name {:en short-name}})
            (when create-visibility
              {:field/create-visibility create-visibility})
            (when constraints
              {:field/constraints constraints})
            (when symbol-attribute
              {:field/secondary-attribute symbol-attribute}))
     overrides)]])

(defn currency-symbol-field-not-secondary
  [attribute label {:keys [default-field-visibility authorization] :as overrides}]
  [[attribute
    (merge-search-capabilities
     (merge {:field/attribute             attribute
             :field/data-type             :data.type/currency-symbol
             :value-type                  :keyword
             :field/default-visibility    (merge {:editor           :editor.type/select
                                                  :grid             :editor.type/select
                                                  :filter           :filter.type/select
                                                  :field-visibility :not-in-use}
                                                 (when default-field-visibility
                                                   {:field-visibility default-field-visibility}))
             :field/default-authorization (merge
                                           {:access/read  :role/user
                                            :access/write :role/user}
                                           (when authorization
                                             authorization))
             :field/translations          {:en label}
             :field/name                  label
             :validation-spec             keyword?
             :options                     currencies/options
             :ui/default-editor-type      :editor.type/select
             :ui/allowed-editor-types     #{:editor.type/select}
             :ui/default-filter-type      :filter.type/select
             :ui/allowed-filter-types     #{:filter.type/select}})
     overrides)]])

(defn calculated-field
  [attribute label
   {:keys [calc-fn data-type composite-attrs default-field-visibility editor-type grid-type options
           filter-type name-attribute value-type entity-types calculation-triggers required-modules]
    :as   overrides}]
  [[attribute
    (merge-search-capabilities
     (merge {:field/attribute             attribute
             :field/data-type             data-type
             :field/default-visibility    (merge {:editor           :editor.type/none
                                                  :grid             :editor.type/text-simple
                                                  :filter           :filter.type/text
                                                  :field-visibility :not-in-use}
                                                 (when default-field-visibility
                                                   {:field-visibility default-field-visibility})
                                                 (when grid-type
                                                   {:grid grid-type})
                                                 (when filter-type
                                                   {:filter filter-type})
                                                 (when editor-type
                                                   {:editor editor-type}))
             :field/default-authorization {:access/read  :role/user
                                           :access/write :role/system}
             :field/composite-attributes  composite-attrs
             :field/calculated-field      calc-fn
             :validation-spec             any?
             :field/translations          {:en label}
             :field/name                  label}
            (when options
              {:options options})
            (when name-attribute
              {:field/name-attribute name-attribute})
            (when value-type
              {:value-type value-type})
            (when entity-types
              {:field/entity-types entity-types})
            (when required-modules
              {:field/required-modules required-modules})
            (when calculation-triggers
              {:calculation-triggers calculation-triggers}))
     overrides)]])

(defn currency-field
  [attribute label
   {:keys [default-field-visibility create-visibility authorization]
    :as   options}]
  (let [symbol-attribute (keyword (namespace attribute) (str (name attribute) "--sym"))]
    [(first
      (currency-value-field attribute label (assoc options :symbol-attribute symbol-attribute)))
     [symbol-attribute
      (merge {:field/attribute             symbol-attribute
              :field/data-type             :data.type/currency-symbol
              :value-type                  :keyword
              :field/related-fields        attribute
              :field/default-visibility    (merge {:editor           :editor.type/select
                                                   :grid             :editor.type/select
                                                   :filter           :filter.type/select
                                                   :field-visibility :not-in-use}
                                                  (when default-field-visibility
                                                    {:field-visibility default-field-visibility}))
              :field/default-authorization (merge
                                            {:access/read  :role/user
                                             :access/write :role/user}
                                            (when authorization
                                              authorization))
              :field/translations          {:en (str label "(currency)")}
              :field/name                  label
              :validation-spec             number?
              :field/secondary-field?      true
              :field/not-clearable?        true
              :options                     currencies/options
              :ui/default-editor-type      :editor.type/select
              :ui/allowed-editor-types     #{:editor.type/select}
              :ui/default-filter-type      :filter.type/select
              :ui/allowed-filter-types     #{:filter.type/select}}
             (when create-visibility
               {:field/create-visibility create-visibility}))]]))

(defn text-multiline-field
  [attribute label
   {:keys [default-field-visibility create-visibility authorization editor-type constraints]
    :as   overrides}]
  [[attribute
    (merge-search-capabilities
     (merge {:field/attribute             attribute
             :field/data-type             :data.type/text-multiline
             :field/default-visibility    (merge
                                           {:editor           :editor.type/textarea
                                            :grid             :editor.type/textarea
                                            :filter           :filter.type/text
                                            :field-visibility :not-in-use}
                                           (when default-field-visibility
                                             {:field-visibility default-field-visibility})
                                           (when editor-type
                                             {:editor editor-type}))
             :field/default-authorization (merge
                                           {:access/read  :role/user
                                            :access/write :role/user}
                                           (when authorization
                                             authorization))
             :field/translations          {:en label}
             :validation-spec             string?
             :field/name                  label
             :ui/default-editor-type      :editor.type/textarea
             :ui/allowed-editor-types     #{:editor.type/textarea}
             :ui/default-filter-type      :filter.type/text
             :ui/allowed-filter-types     #{:filter.type/text}}
            (when create-visibility
              {:field/create-visibility create-visibility})
            (when constraints
              {:field/constraints constraints}))
     overrides)]])

(defn text-edn-field
  [attribute label {:keys [authorization default-field-visibility editor-type filter-edn? hidden-from-config?
                           excluded-from-index? variable-source]
                    :as overrides}]
  [[attribute
    (merge-search-capabilities
     (merge {:field/attribute             attribute
             :field/data-type             :data.type/text-edn
             :field/excluded-from-index?  (if (some? excluded-from-index?)
                                            excluded-from-index?
                                            true)
             :field/default-visibility    (merge
                                           {:editor           :editor.type/none
                                            :grid             :editor.type/none
                                            :filter           :filter.type/none
                                            :field-visibility :not-in-use}
                                           (when default-field-visibility
                                             {:field-visibility default-field-visibility})
                                           (when editor-type
                                             {:editor editor-type}))
             :field/default-authorization (merge
                                           {:access/read  :role/user
                                            :access/write :role/user}
                                           (when authorization
                                             authorization))
             :field/translations          {:en label}
             :field/name                  label
             :validation-spec             string?
             :field/not-importable?       true
             :ui/default-editor-type      (or editor-type :editor.type/none)
             :ui/allowed-editor-types     (if editor-type
                                            #{editor-type}
                                            #{})
             :ui/default-filter-type      :filter.type/text
             :ui/allowed-filter-types     #{:filter.type/text}}
            (when filter-edn?
              {:field/filter-edn? filter-edn?})
            (when hidden-from-config?
              {:field/hidden-from-configuration? true})
            (when variable-source
              {:ui/variable-source variable-source}))
     overrides)]])

(defn image-field
  [attribute label {:keys [authorization default-field-visibility] :as overrides}]
  [[attribute
    (merge-search-capabilities
     (merge {:field/attribute             attribute
             :field/data-type             :data.type/text-url
             :field/default-visibility    (merge
                                           {:editor           :editor.type/text-simple
                                            :grid             :editor.type/text-simple
                                            :filter           :filter.type/text
                                            :field-visibility :not-in-use}
                                           (when default-field-visibility
                                             {:field-visibility default-field-visibility}))
             :field/default-authorization (merge
                                           {:access/read  :role/none
                                            :access/write :role/user}
                                           (when authorization
                                             authorization))
             :field/translations          {:en label}
             :field/name                  label
             :validation-spec             any?
             :ui/default-editor-type      :editor.type/image
             :ui/allowed-editor-types     #{:editor.type/image}
             :ui/default-filter-type      :filter.type/text
             :ui/allowed-filter-types     #{:filter.type/text}})
     overrides)]])

(defn text-simple-field
  [attribute label
   {:keys [default-field-visibility editor-type grid-type filter-type authorization
           name-attribute composite-attributes lower-case-str? not-clearable? hidden-from-configuration?
           mandatory-config constraints composite-text-delim calculation-triggers artificial? not-importable?]
    :as   overrides}]
  [[attribute
    (merge-search-capabilities
     (merge {:field/attribute             attribute
             :field/data-type             :data.type/text-simple
             :field/default-visibility    (merge
                                           {:editor           :editor.type/text-simple
                                            :grid             :editor.type/text-simple
                                            :filter           :filter.type/text
                                            :field-visibility :not-in-use}
                                           (when default-field-visibility
                                             {:field-visibility default-field-visibility})
                                           (when editor-type
                                             {:editor editor-type})
                                           (when grid-type
                                             {:grid grid-type})
                                           (when filter-type
                                             {:filter filter-type}))
             :field/default-authorization (merge
                                           {:access/read  :role/user
                                            :access/write :role/user}
                                           (when authorization
                                             authorization)
                                           (when composite-attributes
                                             {:access/write :role/system}))
             :field/translations          {:en label}
             :field/name                  label
             :validation-spec             string?
             :ui/default-editor-type      :editor.type/text-simple
             :ui/allowed-editor-types     #{:editor.type/text-simple}
             :ui/default-filter-type      :filter.type/text
             :ui/allowed-filter-types     #{:filter.type/text}}
            (when name-attribute
              {:field/name-attribute name-attribute})
            (when composite-attributes
              {:field/composite-attributes composite-attributes
               :field/calculated-field     (partial composite-text-field
                                                    composite-attributes
                                                    (or composite-text-delim " - "))})
            (when lower-case-str?
              {:field/lower-case-str? lower-case-str?})
            (when not-clearable?
              {:field/not-clearable? not-clearable?})
            (when mandatory-config
              {:field/mandatory mandatory-config})
            (when constraints
              {:field/constraints constraints})
            (when artificial?
              {:field/artificial? artificial?})
            (when calculation-triggers
              {:calculation-triggers calculation-triggers})
            (when hidden-from-configuration?
              {:field/hidden-from-configuration? hidden-from-configuration?})
            (when not-importable?
              {:not-importable? not-importable?}))
     overrides)]])

(defn text-simple-multi-field
  [attribute label
   {:keys [default-field-visibility editor-type grid-type filter-type authorization name-attribute
           composite-attributes lower-case-str? not-clearable? calculation-triggers mandatory-config
           constraints composite-text-delim]
    :as   overrides}]
  [[attribute
    (merge-search-capabilities
     (merge {:field/attribute             attribute
             :field/data-type             :data.type/text-multi
             :field/default-visibility    (merge
                                           {:editor           :editor.type/text-simple
                                            :grid             :editor.type/text-simple
                                            :filter           :filter.type/text
                                            :field-visibility :not-in-use}
                                           (when default-field-visibility
                                             {:field-visibility default-field-visibility})
                                           (when editor-type
                                             {:editor editor-type})
                                           (when grid-type
                                             {:grid grid-type})
                                           (when filter-type
                                             {:filter filter-type}))
             :field/default-authorization (merge
                                           {:access/read  :role/user
                                            :access/write :role/user}
                                           (when authorization
                                             authorization)
                                           (when composite-attributes
                                             {:access/write :role/system}))
             :field/translations          {:en label}
             :field/name                  label
             :validation-spec             string?
             :ui/default-editor-type      :editor.type/text-simplearea
             :ui/allowed-editor-types     #{:editor.type/text-simplearea}
             :ui/default-filter-type      :filter.type/text
             :ui/allowed-filter-types     #{:filter.type/text}}
            (when name-attribute
              {:field/name-attribute name-attribute})
            (when composite-attributes
              {:field/composite-attributes composite-attributes
               :field/calculated-field     (partial composite-text-field
                                                    composite-attributes
                                                    (or composite-text-delim " - "))})
            (when lower-case-str?
              {:field/lower-case-str? lower-case-str?})
            (when not-clearable?
              {:field/not-clearable? not-clearable?})
            (when mandatory-config
              {:field/mandatory mandatory-config})
            (when constraints
              {:field/constraints constraints})
            (when calculation-triggers
              {:calculation-triggers calculation-triggers}))
     overrides)]])

(defn url-field
  [attribute label
   {:keys [default-field-visibility editor-type grid-type filter-type authorization
           lower-case-str? not-clearable?
           mandatory-config constraints]
    :as   overrides}]
  [[attribute
    (merge-search-capabilities
     (merge {:field/attribute             attribute
             :field/data-type             :data.type/text-url
             :field/default-visibility    (merge
                                           {:editor           :editor.type/url
                                            :grid             :editor.type/link
                                            :filter           :filter.type/text
                                            :field-visibility :not-in-use}
                                           (when default-field-visibility
                                             {:field-visibility default-field-visibility})
                                           (when editor-type
                                             {:editor editor-type})
                                           (when grid-type
                                             {:grid grid-type})
                                           (when filter-type
                                             {:filter filter-type}))
             :field/default-authorization (merge
                                           {:access/read  :role/user
                                            :access/write :role/user}
                                           (when authorization
                                             authorization))
             :field/translations          {:en label}
             :field/name                  label
             :validation-spec             string?
             :ui/default-editor-type      :editor.type/url
             :ui/allowed-editor-types     #{:editor.type/url}
             :ui/default-filter-type      :filter.type/text
             :ui/allowed-filter-types     #{:filter.type/text}}
            (when lower-case-str?
              {:field/lower-case-str? lower-case-str?})
            (when not-clearable?
              {:field/not-clearable? not-clearable?})
            (when mandatory-config
              {:field/mandatory mandatory-config})
            (when constraints
              {:field/constraints constraints}))
     overrides)]])

(defn text-wysiwyg-field
  [attribute label
   {:keys [default-field-visibility editor-type grid-type filter-type authorization
           lower-case-str? not-clearable?
           mandatory-config constraints]
    :as   overrides}]
  [[attribute
    (merge-search-capabilities
     (merge {:field/attribute             attribute
             :field/data-type             :data.type/text-richtext
             :field/default-visibility    (merge
                                           {:editor           :editor.type/wysiwyg
                                            :grid             :editor.type/wysiwyg
                                            :filter           :filter.type/text
                                            :field-visibility :not-in-use}
                                           (when default-field-visibility
                                             {:field-visibility default-field-visibility})
                                           (when editor-type
                                             {:editor editor-type})
                                           (when grid-type
                                             {:grid grid-type})
                                           (when filter-type
                                             {:filter filter-type}))
             :field/default-authorization (merge
                                           {:access/read  :role/user
                                            :access/write :role/user}
                                           (when authorization
                                             authorization))
             :field/translations          {:en label}
             :field/name                  label
             :validation-spec             string?
             :ui/default-editor-type      :editor.type/wysiwyg
             :ui/allowed-editor-types     #{:editor.type/wysiwyg :editor.type/textarea :editor.type/text-simple}
             :ui/default-filter-type      :filter.type/text
             :ui/allowed-filter-types     #{:filter.type/text}}
            (when lower-case-str?
              {:field/lower-case-str? lower-case-str?})
            (when not-clearable?
              {:field/not-clearable? not-clearable?})
            (when mandatory-config
              {:field/mandatory mandatory-config})
            (when constraints
              {:field/constraints constraints}))
     overrides)]])

(defn long-field
  [attribute label
   {:keys [default-field-visibility authorization editor-type grid-type filter-type] :as overrides}]
  [[attribute
    (merge-search-capabilities
     (merge
      {:field/attribute             attribute
       :field/data-type             :data.type/long
       :field/default-visibility    (merge
                                     {:editor           :editor.type/integer
                                      :grid             :editor.type/integer
                                      :filter           :filter.type/integer
                                      :field-visibility :not-in-use}
                                     (when default-field-visibility
                                       {:field-visibility default-field-visibility})
                                     (when editor-type
                                       {:editor editor-type})
                                     (when grid-type
                                       {:grid grid-type})
                                     (when filter-type
                                       {:filter filter-type}))
       :field/default-authorization (merge
                                     {:access/read  :role/user
                                      :access/write :role/user}
                                     (when authorization
                                       authorization))
       :field/translations          {:en label}
       :field/name                  label
       :validation-spec             number?
       :ui/default-editor-type      :editor.type/integer
       :ui/allowed-editor-types     #{:editor.type/integer}
       :ui/default-filter-type      :filter.type/number
       :ui/allowed-filter-types     #{:filter.type/number}})
     overrides)]])

(defn datetime-field
  [attribute label
   {:keys [default-field-visibility authorization create-visibility
           editor-type grid-type filter-type mandatory-config
           not-importable? hidden-from-configuration?] :as overrides}]
  [[attribute
    (merge-search-capabilities
     (merge {:field/attribute             attribute
             :field/data-type             :data.type/datetime
             :field/default-authorization (merge
                                           {:access/read  :role/user
                                            :access/write :role/user}
                                           (when authorization
                                             authorization))
             :field/default-visibility    (merge
                                           {:editor           :editor.type/datetime
                                            :grid             :editor.type/datetime
                                            :filter           :filter.type/date
                                            :field-visibility :not-in-use}
                                           (when default-field-visibility
                                             {:field-visibility default-field-visibility})
                                           (when editor-type
                                             {:editor editor-type})
                                           (when grid-type
                                             {:grid grid-type})
                                           (when filter-type
                                             {:filter filter-type}))
             :field/translations          {:en label}
             :field/name                  label}
            (when mandatory-config
              {:field/mandatory mandatory-config})
            (when create-visibility
              {:field/create-visibility create-visibility})
            (when not-importable?
              {:not-importable? not-importable?})
            (when hidden-from-configuration?
              {:hidden-from-configuration? hidden-from-configuration?}))
     overrides)]])

(defn date-field
  [attribute label
   {:keys [default-field-visibility authorization create-visibility
           mandatory-config short-name] :as overrides}]
  [[attribute
    (merge-search-capabilities
     (merge {:field/attribute             attribute
             :field/data-type             :data.type/date
             :field/default-authorization (merge
                                           {:access/read  :role/user
                                            :access/write :role/user}
                                           (when authorization
                                             authorization))
             :field/default-visibility    (merge
                                           {:editor           :editor.type/date
                                            :grid             :editor.type/date
                                            :filter           :filter.type/date
                                            :field-visibility :not-in-use}
                                           (when default-field-visibility
                                             {:field-visibility default-field-visibility}))
             :field/translations          {:en label}
             :field/name                  label}
            (when short-name
              {:field/short-name {:en short-name}})
            (when mandatory-config
              {:field/mandatory mandatory-config})
            (when create-visibility
              {:field/create-visibility create-visibility}))
     overrides)]])

(defn calculated-date-difference
  [attribute label end-date-key start-date-key]
  [[attribute
    {:field/attribute             attribute
     :field/data-type             :data.type/integer
     :field/default-visibility    {:editor           :editor.type/integer
                                   :grid             :editor.type/integer
                                   :filter           :filter.type/integer
                                   :field-visibility :not-in-use-blocked}
     :field/composite-attributes  [start-date-key end-date-key]
     :field/calculated-field      (fn [{:keys [entity-data]}]
                                    (let [start-date (get entity-data start-date-key)
                                          end-date (get entity-data end-date-key)]
                                      (cond (not (number? start-date))
                                            nil
                                            (not (number? end-date))
                                            nil
                                            :else
                                            (int (/ (- end-date start-date) (* 1000 3600 24))))))
     :field/default-authorization {:access/read  :role/guest-buy-side
                                   :access/write :role/system}
     :field/translations          {:en label}
     :field/name                  label}]])

(defn percentage-field
  [attribute label
   {:keys [create-visibility default-field-visibility constraints
           authorization short-name] :as overrides}]
  [[attribute
    (merge-search-capabilities
     (merge {:field/attribute             attribute
             :field/data-type             :data.type/percentage
             :field/constraints           (merge
                                           {:constraint/greater-than-inclusive -9007199254740991
                                            :constraint/smaller-than-inclusive 9007199254740991}
                                           (when constraints
                                             constraints))
             :field/default-visibility    (merge
                                           {:editor           :editor.type/percentage
                                            :grid             :editor.type/percentage
                                            :filter           :filter.type/percentage
                                            :field-visibility :not-in-use}
                                           (when default-field-visibility
                                             {:field-visibility default-field-visibility}))
             :field/default-authorization (merge
                                           {:access/read  :role/user
                                            :access/write :role/user}
                                           (when authorization
                                             authorization))
             :field/translations          {:en label}
             :field/name                  label}
            (when short-name
              {:field/short-name {:en short-name}})
            (when create-visibility
              {:field/create-visibility create-visibility}))
     overrides)]])

(defn multiplier-field
  [attribute label {:keys [default-field-visibility authorization] :as overrides}]
  [[attribute
    (merge-search-capabilities
     (merge
      {:field/attribute             attribute
       :field/data-type             :data.type/multiplier
       :field/constraints           {:constraint/greater-than-inclusive -9007199254740991
                                     :constraint/smaller-than-inclusive 9007199254740991}
       :field/default-visibility    (merge
                                     {:editor           :editor.type/multiplier
                                      :grid             :editor.type/multiplier
                                      :filter           :filter.type/multiplier
                                      :field-visibility :not-in-use}
                                     (when default-field-visibility
                                       {:field-visibility default-field-visibility}))
       :field/default-authorization (merge
                                     {:access/read  :role/user
                                      :access/write :role/user}
                                     (when authorization
                                       authorization))
       :field/translations          {:en label}
       :field/name                  label})
     overrides)]])

(defn numeric-field
  [attribute label
   {:keys [default-field-visibility authorization constraints
           create-visibility editor-type grid grid-type filter filter-type
           not-importable? hidden-from-configuration?]
    :as   overrides}]
  [[attribute
    (merge-search-capabilities
     (merge {:field/attribute             attribute
             :field/data-type             :data.type/number
             :field/default-authorization (merge
                                           {:access/read  :role/user
                                            :access/write :role/user}
                                           (when authorization
                                             authorization))
             :field/default-visibility    (merge
                                           {:editor           :editor.type/number
                                            :grid             :editor.type/number
                                            :filter           :filter.type/number
                                            :field-visibility :not-in-use}
                                           (when editor-type
                                             {:editor editor-type})
                                           (when (or grid grid-type)
                                             {:grid (or grid-type grid)})
                                           (when (or filter filter-type)
                                             {:filter (or filter-type filter)})
                                           (when default-field-visibility
                                             {:field-visibility default-field-visibility}))
             :field/translations          {:en label}
             :field/name                  label}
            (when create-visibility
              {:field/create-visibility create-visibility})
            (when constraints
              {:field/constraints constraints})
            (when not-importable?
              {:not-importable? not-importable?})
            (when hidden-from-configuration?
              {:field/hidden-from-configuration? hidden-from-configuration?}))
     overrides)]])

(defn integer-field
  [attribute label {:keys [default-field-visibility authorization constraints editor grid] :as overrides}]
  [[attribute
    (merge-search-capabilities
     (merge {:field/attribute             attribute
             :field/data-type             :data.type/integer
             :field/default-authorization (merge
                                           {:access/read  :role/user
                                            :access/write :role/user}
                                           (when authorization
                                             authorization))
             :field/default-visibility    (merge
                                           {:editor           :editor.type/integer
                                            :grid             :editor.type/integer
                                            :filter           :filter.type/integer
                                            :field-visibility :not-in-use}
                                           (when editor
                                             {:editor editor})
                                           (when grid
                                             {:grid grid})
                                           (when default-field-visibility
                                             {:field-visibility default-field-visibility}))
             :field/translations          {:en label}
             :field/name                  label}
            (when constraints
              {:field/constraints constraints}))
     overrides)]])

(defn keyword-field
  [attribute label options*
   {:keys [authorization default-field-visibility editor-type grid-type filter-type mandatory-config
           not-clearable? custom-sort? field-in-use-filter option-filter-fn
           hidden-from-config? hidden-from-configuration?]
    :as   overrides}]
  [[attribute
    (merge-search-capabilities
     (merge {:field/attribute             attribute
             :field/data-type             :data.type/keyword
             :value-type                  :keyword
             :field/default-visibility    (merge
                                           {:editor           :editor.type/select
                                            :grid             :editor.type/select
                                            :filter           :filter.type/select
                                            :field-visibility :not-in-use}
                                           (when default-field-visibility
                                             {:field-visibility default-field-visibility})
                                           (when editor-type
                                             {:editor editor-type})
                                           (when grid-type
                                             {:grid grid-type})
                                           (when filter-type
                                             {:filter filter-type}))
             :field/default-authorization (merge
                                           {:access/read  :role/user
                                            :access/write :role/user}
                                           (when authorization
                                             authorization))
             :field/translations          {:en label}
             :field/name                  label}
            (when option-filter-fn
              {:field/option-filter-fn option-filter-fn})
            (when custom-sort?
              {:field/custom-sort true})
            (when (contains? overrides :not-clearable?)
              {:field/not-clearable? not-clearable?})
            (when (or hidden-from-config? hidden-from-configuration?)
              {:field/hidden-from-configuration? true})
            (when field-in-use-filter
              {:field/in-use-for-this-data? field-in-use-filter})
            (when mandatory-config
              {:field/mandatory mandatory-config})
            (when options*
              {:options options*}))
     overrides)]])

(defn keyword-multi-field
  [attribute label options*
   {:keys [authorization default-field-visibility mandatory-config
           create-visibility editor-type grid-type]
    :as   overrides}]
  [[attribute
    (merge-search-capabilities
     (merge {:field/attribute             attribute
             :field/data-type             :data.type/keyword-multi
             :value-type                  :keyword
             :field/default-visibility    (merge
                                           {:editor           :editor.type/multi-select
                                            :grid             :editor.type/multi-select
                                            :filter           :filter.type/select
                                            :field-visibility :not-in-use}
                                           (when editor-type
                                             {:editor editor-type})
                                           (when grid-type
                                             {:grid grid-type})
                                           (when default-field-visibility
                                             {:field-visibility default-field-visibility}))
             :field/default-authorization (merge
                                           {:access/read  :role/user
                                            :access/write :role/user}
                                           (when authorization
                                             authorization))
             :field/translations          {:en label}
             :field/name                  label}
            (when mandatory-config
              {:field/mandatory mandatory-config})
            (when options*
              {:options options*})
            (when create-visibility
              {:field/create-visibility create-visibility}))
     overrides)]])

(defn option-field
  [attribute label
   {:keys [default-field-visibility
           authorization
           create-visibility
           editor-type
           grid-type
           data-type
           export-type
           filter-type
           hide-from-webform?]
    :as   overrides}]
  [[attribute
    (merge-search-capabilities
     (merge {:field/attribute             attribute
             :field/data-type             :data.type/ref-single
             :value-type                  :entity
             :create-on-fly?              true
             :field/entity-types          [:entity.type/named-value]
             :named-value-attribute       attribute
             :field/default-visibility    (merge
                                           {:editor           :editor.type/select
                                            :grid             :editor.type/select
                                            :filter           :filter.type/select
                                            :field-visibility :not-in-use}
                                           (when default-field-visibility
                                             {:field-visibility default-field-visibility})
                                           (when editor-type
                                             {:editor editor-type})
                                           (when grid-type
                                             {:grid grid-type})
                                           (when filter-type
                                             {:filter filter-type}))
             :field/default-authorization (merge
                                           {:access/read   :role/user
                                            :access/create :role/user
                                            :access/write  :role/user}
                                           (when authorization
                                             authorization))
             :field/translations          {:en label}
             :field/name                  label}
            (when create-visibility
              {:field/create-visibility create-visibility})
            (when data-type
              {:field/data-type data-type})
            (when export-type
              {:field/export-type export-type})
            (when hide-from-webform?
              {:field/hide-from-webform? hide-from-webform?}))
     overrides)]])

(defn option-field-multi
  [attribute label args]
  (option-field attribute
                label
                (merge
                 {:data-type   :data.type/ref-multi
                  :editor-type :editor.type/multi-select
                  :grid-type   :editor.type/multi-select}
                 args)))

(defn single-ref-field
  [attribute label entity-types
   {:keys [default-field-visibility create-visibility
           editor-type non-empty-editor-type grid-type filter-type not-clearable?
           mandatory-config not-shown-in-create? create-on-fly?
           select-entity-types create-entity-types create-start-value-fn
           option-filter-fn constraints authorization
           field-in-use-filter artificial?
           os-filter-terms-fn
           hidden-from-configuration? not-importable?
           deep-indexing? live-update-paths]
    :as   overrides}]
  (assert-field-options! attribute overrides)
  [[attribute
    (merge-search-capabilities
     (merge {:field/attribute             attribute
             :field/data-type             :data.type/ref-single
             :value-type                  :entity
             :field/default-visibility    (merge
                                           {:editor           :editor.type/select ;; ???
                                            :grid             :editor.type/select
                                            :filter           :filter.type/select
                                            :field-visibility :not-in-use}
                                           (when default-field-visibility
                                             {:field-visibility default-field-visibility})
                                           (when editor-type
                                             {:editor editor-type})
                                           (when grid-type
                                             {:grid grid-type})
                                           (when filter-type
                                             {:filter filter-type})
                                           (when non-empty-editor-type
                                             {:non-empty-editor-type non-empty-editor-type}))
             :field/default-authorization (merge
                                           {:access/read  :role/user
                                            :access/write :role/user}
                                           (when authorization
                                             authorization))
             :field/translations          {:en label}
             :field/name                  label}
            (when entity-types
              {:field/entity-types entity-types})
            (when select-entity-types
              {:field/select-entity-types select-entity-types})
            (when create-entity-types
              {:field/create-entity-types create-entity-types})
            ;; Builds the initial values for an entity created on the fly from
            ;; this field's picker, out of the typed text and the entity being
            ;; edited. Without it the creator receives only the text, and any
            ;; mandatory link on the new entity is left for the user to guess.
            (when create-start-value-fn
              {:field/create-start-value-fn create-start-value-fn})

            (when create-visibility
              {:field/create-visibility create-visibility})
            (when not-shown-in-create?
              {:field/not-shown-in-create? not-shown-in-create?})
            (when not-clearable?
              {:field/not-clearable? not-clearable?})
            (when mandatory-config
              {:field/mandatory mandatory-config})
            (when option-filter-fn
              {:field/option-filter-fn option-filter-fn})
            (when create-on-fly?
              {:create-on-fly? create-on-fly?})
            (when constraints
              {:field/constraints constraints})
            (when field-in-use-filter
              {:field/in-use-for-this-data? field-in-use-filter})
            (when artificial?
              {:field/artificial? artificial?})
            (when hidden-from-configuration?
              {:field/hidden-from-configuration? hidden-from-configuration?})
            (when not-importable?
              {:field/not-importable? not-importable?})
            (when deep-indexing?
              {:field/deep-indexing? deep-indexing?})
            (when (contains? overrides :live-update-paths)
              {:field/live-update-paths live-update-paths})
            (when os-filter-terms-fn
              {:field/os-filter-terms-fn os-filter-terms-fn}))
     overrides)]])

(defn multi-ref-field
  [attribute label entity-types
   {:keys [default-field-visibility authorization option-filter-fn editor-type grid-type
           select-entity-types create-entity-types
           os-filter-terms-fn
           filter-type required-modules create-on-fly?
           deep-indexing? live-update-paths]
    :as   overrides}]
  (assert-field-options! attribute overrides)
  [[attribute
    (merge-search-capabilities
     (merge {:field/attribute             attribute
             :field/data-type             :data.type/ref-multi
             :value-type                  :entity
             :field/default-visibility    (merge
                                           {:editor           :editor.type/multi-select ;; ???
                                            :grid             :editor.type/multi-select
                                            :filter           :filter.type/select
                                            :field-visibility :not-in-use}
                                           (when default-field-visibility
                                             {:field-visibility default-field-visibility})
                                           (when editor-type
                                             {:editor editor-type})
                                           (when grid-type
                                             {:grid grid-type})
                                           (when filter-type
                                             {:filter filter-type}))
             :field/default-authorization (merge
                                           {:access/read  :role/user
                                            :access/write :role/user}
                                           (when authorization
                                             authorization))
             :field/translations          {:en label}
             :field/name                  label}
            (when entity-types
              {:field/entity-types entity-types})
            (when select-entity-types
              {:field/select-entity-types select-entity-types})
            (when create-entity-types
              {:field/create-entity-types create-entity-types})

            (when option-filter-fn
              {:field/option-filter-fn option-filter-fn})
            (when required-modules
              {:field/required-modules required-modules})
            (when create-on-fly?
              {:create-on-fly? create-on-fly?})
            (when deep-indexing?
              {:field/deep-indexing? deep-indexing?})
            (when (contains? overrides :live-update-paths)
              {:field/live-update-paths live-update-paths})
            (when os-filter-terms-fn
              {:field/os-filter-terms-fn os-filter-terms-fn}))
     overrides)]])

(defn boolean-field
  [attribute label {:keys [create-visibility default-field-visibility authorization
                           hidden-from-configuration? not-importable?
                           in-use-for-this-data? editor]
                    :as   overrides}]
  [[attribute
    (merge-search-capabilities
     (merge {:field/attribute             attribute
             :field/data-type             :data.type/boolean
             :field/default-authorization (merge
                                           {:access/read  :role/user
                                            :access/write :role/user}
                                           (when authorization
                                             authorization))
             :field/default-visibility    (merge
                                           {:editor           :editor.type/toggle
                                            :grid             :editor.type/toggle
                                            :filter           :filter.type/boolean
                                            :field-visibility :not-in-use}
                                           (when default-field-visibility
                                             {:field-visibility default-field-visibility})
                                           (when editor
                                             {:editor editor}))
             :field/translations          {:en label}
             :field/name                  label}
            (when create-visibility
              {:field/create-visibility create-visibility})
            (when in-use-for-this-data?
              {:field/in-use-for-this-data? in-use-for-this-data?})
            (when hidden-from-configuration?
              {:hidden-from-configuration? hidden-from-configuration?})
            (when not-importable?
              {:not-importable? not-importable?}))
     overrides)]])

(defn tag-field
  [attribute label args]
  ;;TODO move common.schema.field/tag-field? to fix circular dependency and use here:
  (assert (string/starts-with? (name attribute) "tag-")
          "Attribute Must start with 'tag-'")
  (option-field-multi attribute label args))
